import React, { useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { makeStyles } from '@material-ui/core/styles';
import {
  Button,
  List,
  Grid,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog as MuiDialog,
  DialogTitle as MuiDialogTitle,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';

import Dialog from '../components/Dialog';
import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DateTimePicker from '../components/DateTimePicker';
import Checkbox from '../components/Checkbox';
import Select from '../components/Select';
import MultiSelect from '../components/MultiSelect';
import ColorPicker from '../components/ColorPicker';

import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';
import useDiaryData from '../hooks/useDiaryData';
import useMainContext from '../hooks/useMainContext';

import dateTimeUtils from '../components/DateTimePicker/utils';
import { maxWidth } from '../components/configs';

import prospects from '../api/prospects';
import tries from '../api/tries';
import submissions from '../api/submissions';
import events from '../api/events';

import {
  colorOptions,
  repeatOptions,
  repeatPeriodOptions,
  eventStatusOptions,
  appointmentCountOptions,
} from '../configs/events';

const queryParamKey = 'update-event';
const dateFormat = 'YYYY-MM-DD';
const dateTimeFormat = 'YYYY-MM-DD HH:mm';
const emptyValue = appointmentCountOptions[0].value;

// pass null for non-repeat event
const updateTypeOptions = [
  { label: 'This event', value: 'CURRENT' },
  { label: 'This and following events', value: 'FOLLOWING' },
];

// pass null for non-repeat event
const deleteTypeOptions = [
  { label: 'All events', value: 'All' },
  { label: 'This event', value: 'CURRENT' },
  { label: 'This and following events', value: 'FOLLOWING' },
];

const generateDefaultState = selectedDate => {
  const currentMoment = moment();
  const defaultMoment = moment(selectedDate)
    .hour(currentMoment.hour())
    .minute(currentMoment.minute());

  return {
    name: '',
    contact: '',
    status: '0',
    appointmentCount: appointmentCountOptions[0].value,
    startAt: dateTimeUtils.generateDefaultValue(
      defaultMoment.format(dateTimeFormat),
      30,
      true
    ),
    endAt: dateTimeUtils.generateDefaultValue(
      moment(defaultMoment).add(1, 'hour').format(dateTimeFormat),
      30,
      true
    ),
    isAllDay: false,
    remarks: '',
    repeat: repeatOptions[0].value,
    repeatFrequency: 1,
    repeatPeriod: repeatPeriodOptions[0].value,
    repeatEndDate: null,
    color: colorOptions[0],
    tries: [],
    submissions: [],
    prospects: [],
  };
};

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth,
    margin: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  customRepeatWrapper: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-around',
  },
  submitButton: {
    width: '100%',
    marginTop: 20,
    marginBottom: 10,
    color: '#fff',
    borderRadius: 6,
    fontWeight: 600,
    backgroundColor: theme.palette.success.main,
    '&:hover': {
      backgroundColor: theme.palette.success.dark,
    },
  },
  deleteButton: {
    width: '100%',
    marginTop: 10,
    marginBottom: 20,
    borderRadius: 6,
    fontWeight: 600,
    color: theme.palette.error.main,
  },
  loading: {
    marginTop: 50,
    marginBottom: 50,
    color: '#c7c7c7',
  },
  list: {
    marginTop: 10,
    width: '100%',
    backgroundColor: '#eaeaea',
    borderRadius: theme.shape.borderRadius,
  },
  dialogContentWrapper: {
    display: 'flex',
    justifyContent: 'space-evenly',
    marginTop: 10,
    marginBottom: 10,
  },
}));

function UpdateEventDialog() {
  const classes = useStyles();
  const [conflictEvents, setConflictEvents] = useState([]);
  const [updateDialog, setUpdateDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [originEvent, setOriginEvent] = useState(false);
  // eslint-disable-next-line
  const [diary, _, handleSetDate, handleManualFetch] = useDiaryData();
  const [disabled, setDisabled] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [queryParams, setQueryParams] = useQueryParams();
  const [state, _setState] = useState(generateDefaultState(diary.selectedDate));
  const [error, setError] = useState({});
  const handleSnackbarChange = useSnackbarText();
  const {
    eventDetailUpdate,
    openCreateProspectAfterLoad,
    handleChange: handleMainContextChange,
  } = useMainContext();

  const queryValue = queryParams[queryParamKey];

  const setState = useCallback(newState => {
    _setState(prevState => ({
      ...prevState,
      ...newState,
    }));
  }, []);

  const handleChange = type => (event, value) => {
    if (value !== undefined) setState({ [type]: value });
    else setState({ [type]: event.target.value });
  };

  const handleDateTimeChange = type => (event, value) => {
    setState({ [type]: value });
    if (type === 'startAt') {
      setState({ endAt: moment(value).add(1, 'hour') });
    }
  };

  const handleCheckboxChange = (event, value) => {
    setState({ isAllDay: value });
  };

  const handleValueCheck = () => {
    let noError = true;
    let error = {};
    if (!state.name) {
      noError = false;
      error.name = 'Invalid value.';
    }
    setError(error);
    return noError;
  };

  const handleRepeatEndCheckbox = (event, value) => {
    if (value) {
      setState({ repeatEndDate: null });
    } else {
      setState({ repeatEndDate: moment().add(1, 'week').format(dateFormat) });
    }
  };

  const handleMultiSelectChange = event => {
    const newValue = event.target.value;
    setState({ status: newValue.length ? newValue.join(',') : '0' });
  };

  const handleSubmit = updateType => async () => {
    setUpdateDialog(false);
    setQueryParams({ conflict: null });
    const isRepeat = Boolean(state.repeat !== repeatOptions[0].value);
    const isCustomRepeat = Boolean(state.repeat === repeatOptions[5].value);

    setDisabled(true);
    const eventResult = await events.updateEvent({
      name: state.name,
      contact: state.contact,
      status: state.status,
      appointmentCount:
        state.appointmentCount === emptyValue ? null : state.appointmentCount,
      startAt: new Date(moment(state.startAt).format()),
      endAt: new Date(moment(state.endAt).format()),
      isAllDay: state.isAllDay,
      remarks: state.remarks,
      isRepeat: isRepeat,
      isCustomRepeat: isCustomRepeat,
      repeatFrequency: isRepeat
        ? isCustomRepeat
          ? state.repeatFrequency
          : 1
        : null,
      repeatPeriod: isRepeat
        ? isCustomRepeat
          ? state.repeatPeriod
          : state.repeat
        : null,
      repeatEndDate: isRepeat
        ? state.repeatEndDate && new Date(state.repeatEndDate)
        : null,
      color: state.color,
      selectedStartDate: eventDetailUpdate.selectedStartDate,
      exceptionID: eventDetailUpdate.exceptionID,
      eventID: eventDetailUpdate.eventID,
      updateType,
    });

    if (!eventResult.success) {
      setDisabled(false);
      return handleSnackbarChange({
        text: eventResult.data.message,
        variant: 'error',
      });
    }

    if (state.prospects.length) {
      const prospectResult = await prospects.updateProspectEventID({
        selectedStartDate: eventDetailUpdate.selectedStartDate,
        eventID: eventResult.data.eventID,
        prospectIDs: state.prospects.map(e => e.prospectID),
      });

      if (!prospectResult.success) {
        setDisabled(false);
        return handleSnackbarChange({
          text: prospectResult.data.message,
          variant: 'error',
        });
      }
    }

    if (state.tries.length) {
      const tryResult = await tries.updateTryEventID({
        selectedStartDate: eventDetailUpdate.selectedStartDate,
        eventID: eventResult.data.eventID,
        tryIDs: state.tries.map(e => e.tryID),
      });

      if (!tryResult.success) {
        setDisabled(false);
        return handleSnackbarChange({
          text: tryResult.data.message,
          variant: 'error',
        });
      }
    }

    if (state.submissions.length) {
      const submissionResult = await submissions.updateSubmissionEventID({
        selectedStartDate: eventDetailUpdate.selectedStartDate,
        eventID: eventResult.data.eventID,
        submissionIDs: state.submissions.map(e => e.submissionID),
      });

      if (!submissionResult.success) {
        setDisabled(false);
        return handleSnackbarChange({
          text: submissionResult.data.message,
          variant: 'error',
        });
      }
    }

    handleSnackbarChange({ text: 'Event updated', variant: 'success' });
    setDisabled(false);
    handleManualFetch();
    setQueryParams({ [queryParamKey]: null });
    handleMainContextChange({ eventDetailUpdate: null });
  };

  const handleUpdatePress = async () => {
    if (!handleValueCheck()) {
      return handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
    }

    const isRepeat = Boolean(state.repeat !== repeatOptions[0].value);
    if (!isRepeat) {
      setDisabled(true);
      const { data, success } = await events.checkConflict({
        eventID: eventDetailUpdate.eventID,
        startAt: new Date(moment(state.startAt).format()),
        endAt: new Date(moment(state.endAt).format()),
        isAllDay: state.isAllDay,
      });
      setDisabled(false);

      if (success) {
        if (data.conflictEvents.length) {
          setQueryParams({ conflict: 'true' });
          setConflictEvents(data.conflictEvents);
        } else {
          handleSubmit(null)();
        }
      } else {
        return handleSnackbarChange({ text: data.message, variant: 'error' });
      }
    } else {
      if (originEvent.isRepeat) setUpdateDialog(true);
      else handleSubmit(null)();
    }
  };

  const handleDeleteEvent = deleteType => async () => {
    setDeleteDialog(false);
    setDeleteDisabled(true);
    setQueryParams({ 'delete-event': null });

    const { success, data } = await events.deleteEvent({
      selectedStartDate: eventDetailUpdate.selectedStartDate,
      exceptionID: eventDetailUpdate.exceptionID,
      eventID: eventDetailUpdate.eventID,
      deleteType,
    });
    if (!success) {
      setDeleteDisabled(false);
      return handleSnackbarChange({ text: data.message, variant: 'error' });
    }

    handleSnackbarChange({ text: 'Event deleted', variant: 'success' });
    setDeleteDisabled(false);
    handleManualFetch();
    setQueryParams({ [queryParamKey]: null });
    handleMainContextChange({ eventDetailUpdate: null });
  };

  const handleDeletePress = () => {
    if (originEvent.isRepeat) setDeleteDialog(true);
    else setQueryParams({ 'delete-event': 'true' });
  };

  // prospects
  const handleProspectClose = details => {
    const prospectState = state.prospects;
    const index = prospectState
      .map(e => e.prospectID)
      .indexOf(details.prospectID);

    if (index === -1) prospectState.push(details);
    else prospectState[index] = details;

    handleChange('prospects')(null, prospectState);
    handleMainContextChange({ onProspectClose: null });

    // update event id immediately
    prospects.updateProspectEventID({
      selectedStartDate: eventDetailUpdate.selectedStartDate,
      eventID: eventDetailUpdate.eventID,
      prospectIDs: prospectState.map(e => e.prospectID),
    });
  };

  const handleCreateProspectOpen = () => {
    setQueryParams({ 'create-prospect': true });
    handleMainContextChange({ onProspectClose: handleProspectClose });
  };

  const handleUpdateProspectOpen = prospectID => () => {
    setQueryParams({ 'update-prospect': prospectID });
    handleMainContextChange({ onProspectClose: handleProspectClose });
  };

  const handleDeleteProspect = prospectID => async () => {
    let { data, success } = await prospects.deleteProspect({ prospectID });
    if (!success) {
      return handleSnackbarChange({ text: data.message, variant: 'error' });
    } else {
      handleSnackbarChange({ text: 'Prospect deleted', variant: 'success' });
    }

    const { prospects: stateProspects } = state;
    const index = stateProspects
      .map(e => e.prospectID)
      .indexOf(parseInt(prospectID));

    if (index === -1) return;
    else stateProspects.splice(index, 1);

    handleChange('prospects')(null, stateProspects);
    setQueryParams({ prospectDeleteID: null });
  };

  // tries
  const handleTryClose = details => {
    const tryState = state.tries;
    const index = tryState.map(e => e.tryID).indexOf(details.tryID);

    if (index === -1) tryState.push(details);
    else tryState[index] = details;

    handleChange('tries')(null, tryState);
    handleMainContextChange({ onTryClose: null });

    // update event id immediately
    tries.updateTryEventID({
      selectedStartDate: eventDetailUpdate.selectedStartDate,
      eventID: eventDetailUpdate.eventID,
      tryIDs: tryState.map(e => e.tryID),
    });
  };

  const handleCreateTryOpen = () => {
    setQueryParams({ 'create-tracking': true });
    handleMainContextChange({ onTryClose: handleTryClose });
  };

  const handleUpdateTryOpen = tryID => () => {
    setQueryParams({ 'update-tracking': tryID });
    handleMainContextChange({ onTryClose: handleTryClose });
  };

  const handleDeleteTry = tryID => async () => {
    let { data, success } = await tries.deleteTry({ tryID });
    if (!success) {
      return handleSnackbarChange({ text: data.message, variant: 'error' });
    } else {
      handleSnackbarChange({
        text: 'Prospect tracking deleted',
        variant: 'success',
      });
    }

    const { tries: stateTries } = state;
    const tryIndex = stateTries.map(e => e.tryID).indexOf(parseInt(tryID));

    if (tryIndex === -1) return;
    else stateTries.splice(tryIndex, 1);

    handleChange('tries')(null, stateTries);
    setQueryParams({ tryDeleteID: null });
  };

  // submissions
  const handleSubmissionClose = details => {
    const submissionState = state.submissions;
    const index = submissionState
      .map(e => e.submissionID)
      .indexOf(details.submissionID);

    if (index === -1) submissionState.push(details);
    else submissionState[index] = details;

    handleChange('submissions')(null, submissionState);
    handleMainContextChange({ onSubmissionClose: null });

    submissions.updateSubmissionEventID({
      selectedStartDate: eventDetailUpdate.selectedStartDate,
      eventID: eventDetailUpdate.eventID,
      submissionIDs: submissionState.map(e => e.submissionID),
    });
  };

  const handleCreateSubmissionOpen = () => {
    setQueryParams({ 'create-submission': true });
    handleMainContextChange({ onSubmissionClose: handleSubmissionClose });
  };

  const handleUpdateSubmissionOpen = submissionID => () => {
    setQueryParams({ 'update-submission': submissionID });
    handleMainContextChange({ onSubmissionClose: handleSubmissionClose });
  };

  const handleDeleteSubmission = submissionID => async () => {
    let { data, success } = await submissions.deleteSubmission({
      submissionID,
    });
    if (!success) {
      return handleSnackbarChange({ text: data.message, variant: 'error' });
    } else {
      handleSnackbarChange({ text: 'Submission deleted', variant: 'success' });
    }

    const { submissions: stateSubmissions } = state;
    const index = stateSubmissions
      .map(e => e.submissionID)
      .indexOf(parseInt(submissionID));

    if (index === -1) return;
    else stateSubmissions.splice(index, 1);

    handleChange('submissions')(null, stateSubmissions);
    setQueryParams({ submissionDeleteID: null });
  };

  useEffect(() => {
    _setState(generateDefaultState(diary.selectedDate));
    if (!queryValue) {
      handleMainContextChange({ eventDetailUpdate: null });
      setQueryParams({ conflict: null });
    }
  }, [queryValue, diary.selectedDate, handleMainContextChange, setQueryParams]);

  useEffect(() => {
    if (!eventDetailUpdate) {
      handleMainContextChange({ eventDetailUpdate: null });
      return setQueryParams({ [queryParamKey]: null });
    }

    const { eventID, exceptionID, selectedStartDate } = eventDetailUpdate;
    const getData = async () => {
      let { data, success } = await events.getEvent({
        eventID,
        exceptionID,
        selectedStartDate,
      });

      if (success) {
        setLoaded(true);
        const eventDetails = data.event;
        setState({
          name: eventDetails.name,
          contact: eventDetails.contact,
          status: eventDetails.status,
          appointmentCount:
            eventDetails.appointmentCount || appointmentCountOptions[0].value,
          startAt: moment(eventDetails.startAt).format(dateTimeFormat),
          endAt: moment(eventDetails.endAt).format(dateTimeFormat),
          isAllDay: eventDetails.isAllDay,
          remarks: eventDetails.remarks,
          repeatFrequency: eventDetails.isRepeat
            ? eventDetails.repeatFrequency
            : 1,
          repeatPeriod: eventDetails.isRepeat
            ? eventDetails.repeatPeriod
            : repeatPeriodOptions[0].value,
          repeatEndDate: eventDetails.repeatEndDate
            ? moment(eventDetails.repeatEndDate).format(dateFormat)
            : null,
          color: eventDetails.color,
          tries: eventDetails.tries,
          prospects: eventDetails.prospects,
          submissions: eventDetails.submissions,
          repeat: eventDetails.isRepeat
            ? eventDetails.repeatFrequency !== 1
              ? repeatOptions[5].value
              : eventDetails.repeatPeriod
            : repeatOptions[0].value,
        });
        setOriginEvent(eventDetails);
      } else {
        return handleSnackbarChange({ text: data.message, variant: 'error' });
      }
    };

    if (eventID) getData();
  }, [
    eventDetailUpdate,
    setQueryParams,
    setState,
    handleSnackbarChange,
    handleMainContextChange,
  ]);

  useEffect(() => {
    if (!loaded || !openCreateProspectAfterLoad || !eventDetailUpdate) return;

    setQueryParams({ 'create-prospect': true });
    handleMainContextChange({
      openCreateProspectAfterLoad: false,
      onProspectClose: handleProspectClose,
    });
  }, [loaded, openCreateProspectAfterLoad, eventDetailUpdate]);

  return (
    <FullScreenDialog title="Update Event" queryParamKey={queryParamKey}>
      {!loaded && <div className={classes.loading}>loading</div>}
      {loaded && (
        <div className={classes.container}>
          <TextField
            label="Name*"
            value={state.name}
            onChange={handleChange('name')}
            error={Boolean(error.name)}
            helperText={error.name}
          />

          <TextField
            label="Contact"
            value={state.contact}
            onChange={handleChange('contact')}
            error={Boolean(error.contact)}
            helperText={error.contact}
          />

          <MultiSelect
            label="Status"
            value={state.status ? state.status.split(',') : []}
            options={eventStatusOptions
              // disable `Non Face To Face`
              .filter(option => option.value !== '12')}
            onChange={handleMultiSelectChange}
          />

          <Grid container spacing={1}>
            <Grid xs={3} item>
              <Select
                label="Apt"
                options={appointmentCountOptions}
                value={state.appointmentCount}
                onChange={handleChange('appointmentCount')}
              />
            </Grid>

            <Grid xs={9} item>
              <TextField
                label="Remarks"
                value={state.remarks}
                onChange={handleChange('remarks')}
                error={Boolean(error.remarks)}
              />
            </Grid>
          </Grid>

          <DateTimePicker
            label="Start Date"
            isDayHighlighted={date =>
              date === moment(state.endAt).format(dateFormat)
            }
            value={state.startAt}
            onChange={handleDateTimeChange('startAt')}
            showTime={!state.isAllDay}
          />

          <DateTimePicker
            label="End Date"
            isDayHighlighted={date =>
              date === moment(state.startAt).format(dateFormat)
            }
            value={state.endAt}
            onChange={handleDateTimeChange('endAt')}
            showTime={!state.isAllDay}
          />

          <Checkbox
            label="All Day"
            value={state.isAllDay}
            onChange={handleCheckboxChange}
          />

          <Select
            label="Repeat"
            options={repeatOptions}
            value={state.repeat}
            onChange={handleChange('repeat')}
          />

          {/* CUSTOM Repeat */}
          {state.repeat === repeatOptions[5].value && (
            <div className={classes.customRepeatWrapper}>
              <TextField
                label="Repeat Frequency"
                value={state.repeatFrequency}
                onChange={handleChange('repeatFrequency')}
                error={Boolean(error.repeatFrequency)}
                helperText={error.repeatFrequency}
                type="number"
                maxWidth="45%"
              />

              <Select
                label="Repeat Period"
                options={repeatPeriodOptions}
                value={state.repeatPeriod}
                onChange={handleChange('repeatPeriod')}
                maxWidth="45%"
              />
            </div>
          )}

          {state.repeat !== repeatOptions[0].value && (
            <Checkbox
              label="Repeat never end"
              value={!state.repeatEndDate}
              onChange={handleRepeatEndCheckbox}
            />
          )}

          {Boolean(state.repeatEndDate) && (
            <DateTimePicker
              label="Repeat End Date"
              value={state.repeatEndDate}
              onChange={handleDateTimeChange('repeatEndDate')}
            />
          )}

          <ColorPicker
            value={state.color}
            options={colorOptions}
            onChange={handleChange('color')}
          />

          {/* Prospect */}
          <List className={classes.list} dense>
            <ListItem onClick={handleCreateProspectOpen} button>
              <ListItemText primary="+ Create Prospect List" />
            </ListItem>
            {state.prospects.map(e => (
              <ListItem
                key={e.prospectID}
                onClick={handleUpdateProspectOpen(e.prospectID)}
                button
              >
                <ListItemText primary={e.name} />
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={() =>
                      setQueryParams({ prospectDeleteID: e.prospectID })
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {/* Try to close */}
          <List className={classes.list} dense>
            <ListItem onClick={handleCreateTryOpen} button>
              <ListItemText primary="+ Create Prospect Tracking" />
            </ListItem>
            {state.tries.map(e => (
              <ListItem
                key={e.tryID}
                onClick={handleUpdateTryOpen(e.tryID)}
                button
              >
                <ListItemText primary={e.name} />
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={() => setQueryParams({ tryDeleteID: e.tryID })}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {/* Submission */}
          <List className={classes.list} dense>
            <ListItem onClick={handleCreateSubmissionOpen} button>
              <ListItemText primary="+ Create Submission" />
            </ListItem>
            {state.submissions.map(e => (
              <ListItem
                key={e.submissionID}
                onClick={handleUpdateSubmissionOpen(e.submissionID)}
                button
              >
                <ListItemText primary={e.lifeAssured} />
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={() =>
                      setQueryParams({ submissionDeleteID: e.submissionID })
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          <Button
            className={classes.submitButton}
            onClick={handleUpdatePress}
            disabled={disabled}
          >
            Update
          </Button>

          <Button
            className={classes.deleteButton}
            onClick={handleDeletePress}
            disabled={deleteDisabled}
          >
            Delete
          </Button>
        </div>
      )}

      <MuiDialog
        onClose={() => setUpdateDialog(false)}
        open={updateDialog}
        aria-labelledby="dialog-title"
      >
        <MuiDialogTitle>Update recurring event</MuiDialogTitle>
        <List>
          {updateTypeOptions.map(e => (
            <ListItem key={e.value} onClick={handleSubmit(e.value)} button>
              <ListItemText primary={e.label} />
            </ListItem>
          ))}
        </List>
      </MuiDialog>

      <MuiDialog
        onClose={() => setDeleteDialog(false)}
        open={deleteDialog}
        aria-labelledby="dialog-title"
      >
        <MuiDialogTitle>Delete recurring event</MuiDialogTitle>
        <List>
          {deleteTypeOptions.map(e => (
            <ListItem key={e.value} onClick={handleDeleteEvent(e.value)} button>
              <ListItemText primary={e.label} />
            </ListItem>
          ))}
        </List>
      </MuiDialog>

      {/* Prospect */}
      <Dialog
        title="Delete this prospect list?"
        queryParamKey="prospectDeleteID"
      >
        <div className={classes.dialogContentWrapper}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDeleteProspect(queryParams.prospectDeleteID)}
          >
            Delete
          </Button>
          <Button onClick={() => setQueryParams({ prospectDeleteID: null })}>
            Cancel
          </Button>
        </div>
      </Dialog>

      {/* Try to close */}
      <Dialog title="Delete this tracking?" queryParamKey="tryDeleteID">
        <div className={classes.dialogContentWrapper}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDeleteTry(queryParams.tryDeleteID)}
          >
            Delete
          </Button>
          <Button onClick={() => setQueryParams({ tryDeleteID: null })}>
            Cancel
          </Button>
        </div>
      </Dialog>

      {/* Submission */}
      <Dialog
        title="Delete this submission?"
        queryParamKey="submissionDeleteID"
      >
        <div className={classes.dialogContentWrapper}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDeleteSubmission(queryParams.submissionDeleteID)}
          >
            Delete
          </Button>
          <Button onClick={() => setQueryParams({ submissionDeleteID: null })}>
            Cancel
          </Button>
        </div>
      </Dialog>

      {/* Event conflict */}
      <Dialog title="Conflict with following events" queryParamKey="conflict">
        <List dense>
          {conflictEvents.map((e, i) => (
            <ListItem key={i}>
              <ListItemText
                primary={e.name}
                secondary={`${e.date} (${
                  e.isAllDay ? 'All Day' : e.startTime + '-' + e.endTime
                })`}
              />
            </ListItem>
          ))}
        </List>

        <div className={classes.dialogContentWrapper}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit(null)}
          >
            Proceed
          </Button>
          <Button onClick={() => setQueryParams({ conflict: null })}>
            Cancel
          </Button>
        </div>
      </Dialog>

      {/* Event */}
      <Dialog title="Delete this event?" queryParamKey="delete-event">
        <div className={classes.dialogContentWrapper}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleDeleteEvent(null)}
          >
            Delete
          </Button>
          <Button onClick={() => setQueryParams({ 'delete-event': null })}>
            Cancel
          </Button>
        </div>
      </Dialog>
    </FullScreenDialog>
  );
}

export default UpdateEventDialog;
